import { FC, useCallback } from 'react';
import { useRouter } from 'shared-routing';

import SectionContainer from '@common/components/SectionContainer';
import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import { useCouldNotCheckFeedback } from '@common/hooks/useCouldNotCheckFeedback';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { JourneyPickerV2 } from '@features/JourneyPickerV2';
import { openJourneyPickerDates } from '@features/JourneyPickerV2/openJourneyPickerDates';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useJourneyPickerAvailability } from '@features/JourneyPickerV2/useJourneyPickerAvailability';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useRoomPageSettings } from '@features/rooms';
import { useTranslations } from '@lang';

import { AccommodationListTemplate } from 'voyager';
import type { JourneyPickerSearchParams } from 'voyager';

import { parseDateToString } from '@utils/helpers';
import { useRoomsList } from './useRoomsList';
import { RoomsV2Props } from './RoomsV2.types';

export const RoomsV2: FC<RoomsV2Props> = ({ journeyParams, language, changeJourneyParam }) => {
  const router = useRouter();
  const { t } = useTranslations();

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  const {
    listItems: roomItems,
    isFetching,
    isAvailabilityUnknown,
    isRetrying,
    retry,
  } = useRoomsList(journeyParams, language);
  const listItems = isAvailabilityUnknown ? [] : roomItems;
  const noResultsFeedback = useCouldNotCheckFeedback(isAvailabilityUnknown, retry, isRetrying);

  const { content } = useRoomPageSettings(language, t('roomsListing.sectionTitle'));

  const { availability, isLoadingAvailability, onMonthChange } = useJourneyPickerAvailability({
    adults: journeyParams.adults ?? 0,
    children: journeyParams.children ?? 0,
    initialMonth: journeyParams.fromDate?.format('YYYY-MM-DD'),
  });

  const handleSearchComplete = useCallback(
    (params: JourneyPickerSearchParams) => {
      const adults = params.adults;
      const children = params.children ?? 0;
      changeJourneyParam('rooms', [{ roomNumber: 1, adults, children }]);
      changeJourneyParam('childAges', []);
      router.push({
        pathname: Routes.ROOM_LIST,
        query: {
          fromDate: parseDateToString(params.fromDate),
          toDate: params.toDate ? parseDateToString(params.toDate) : undefined,
          adults: String(adults),
          children: String(children),
        },
      });
    },
    [router, changeJourneyParam]
  );

  const handleEditSearch = useCallback(() => {
    openJourneyPickerDates();
  }, []);

  const handleSelectRoom = useCallback(
    (id: string) => {
      router.push({
        pathname: Routes.ROOM_DETAIL,
        query: {
          id,
          fromDate: parseDateToString(journeyParams.fromDate),
          toDate: parseDateToString(journeyParams.toDate),
          adults: String(journeyParams.adults),
          children: String(journeyParams.children),
        },
      });
    },
    [
      router,
      journeyParams.fromDate,
      journeyParams.toDate,
      journeyParams.adults,
      journeyParams.children,
    ]
  );

  return (
    <EntitiesListingWrapper>
      <JourneyPickerOuterWrapper>
        <JourneyPickerInnerWrapper>
          <JourneyPickerV2
            dateMode="range"
            dateContext="stay"
            showGuests
            categories={navCategories}
            category="rooms"
            onCategoryChange={handleNavCategoryChange}
            onSearchComplete={handleSearchComplete}
            availability={availability}
            isLoadingAvailability={isLoadingAvailability}
            onMonthChange={onMonthChange}
          />
        </JourneyPickerInnerWrapper>
      </JourneyPickerOuterWrapper>
      <SectionContainer>
        <Spinner showDelayMessage isActive={isFetching && listItems.length > 0} />
        <AccommodationListTemplate
          items={listItems}
          onSelectItem={handleSelectRoom}
          isLoading={isFetching && listItems.length === 0 && !isRetrying}
          titleLabel={content.title}
          fromPriceLabel={t('common.from')}
          soldOutLabel={t('common.unavailable')}
          noResultsFeedback={noResultsFeedback}
          noResultsLabel={
            isAvailabilityUnknown ? t('spa.integrationUnavailable') : t('roomsListing.noResults')
          }
          noImageLabel={t('basket.noImageLabel')}
          editSearchLabel={t('common.editSearch')}
          onEditSearch={handleEditSearch}
          columns={2}
          locale={language}
        />
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
